import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStore } from '@/store';
import { signInWithGoogle, isFirebaseInitialized } from '@/lib/firebase';
import { acceptInvitation } from '@/lib/api';
import { Clock, ArrowRight } from 'lucide-react';

export default function Invite() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { auth, setAuth } = useStore();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<{
    ownerName: string;
    ownerAvatar: string;
  } | null>(null);
  const [accepting, setAccepting] = useState(false);
  
  useEffect(() => {
    // Fetch invitation info
    const fetchInvitation = async () => {
      try {
        const response = await fetch(`/api/shares/invitation/${code}`);
        const data = await response.json();
        
        if (data.error) {
          setError(data.message);
        } else {
          setInvitation(data.invitation);
        }
      } catch (err) {
        setError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };
    
    if (code) {
      fetchInvitation();
    }
  }, [code]);
  
  const handleSignIn = async () => {
    try {
      const user = await signInWithGoogle();
      setAuth({
        isAuthenticated: true,
        user: {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
        },
      });
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };
  
  const handleAccept = async () => {
    if (!code) return;
    
    setAccepting(true);
    try {
      await acceptInvitation(code);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-2xl font-bold">
            <Clock className="w-8 h-8 text-primary" />
            ClockSync
          </Link>
        </div>
        
        <div className="card p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              <p className="mt-4 text-text-muted">Loading invitation...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">❌</div>
              <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
              <p className="text-text-muted mb-6">{error}</p>
              <Link to="/" className="btn-primary">
                Go to Dashboard
              </Link>
            </div>
          ) : invitation ? (
            <div className="text-center">
              <div className="text-6xl mb-4">🤝</div>
              <h2 className="text-xl font-semibold mb-2">You're Invited!</h2>
              
              <div className="flex items-center justify-center gap-3 my-6 p-4 bg-bg rounded-lg">
                <div className="text-4xl">{invitation.ownerAvatar || '👤'}</div>
                <div className="text-left">
                  <div className="font-medium">{invitation.ownerName}</div>
                  <div className="text-sm text-text-muted">wants to share their time with you</div>
                </div>
              </div>
              
              {auth.isAuthenticated ? (
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {accepting ? (
                    'Accepting...'
                  ) : (
                    <>
                      Let Me Accept
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <div>
                  <p className="text-sm text-text-muted mb-4">
                    Sign in to accept this invitation and share times with {invitation.ownerName}
                  </p>
                  
                  {isFirebaseInitialized() ? (
                    <button onClick={handleSignIn} className="btn-primary w-full flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Let Me Sign In
                    </button>
                  ) : (
                    <div className="text-sm text-text-dim p-3 bg-bg rounded-lg">
                      Firebase not configured
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-6 pt-4 border-t border-white/10">
                <Link to="/" className="text-sm text-text-muted hover:text-text">
                  ← Back to Dashboard
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
